import { useState, useEffect } from "react";
import { Modal, Button, Spinner } from "react-bootstrap";
import { Image, AlertCircle } from "lucide-react";

interface ScoreSliderImagesModalProps {
    isOpen: boolean;
    onClose: () => void;
    fileUrl: string | null;
}

export const ScoreSliderImagesModal = ({
    isOpen,
    onClose,
    fileUrl,
}: ScoreSliderImagesModalProps) => {
    const [isLoading, setIsLoading] = useState(false);
    const [imageError, setImageError] = useState(false);

    // Reset state when the image changes or modal reopens
    useEffect(() => {
        setImageError(false);
        setIsLoading(!!fileUrl);
    }, [fileUrl, isOpen]);

    return (
        <Modal
            show={isOpen}
            onHide={onClose}
            size="lg"
            centered
            backdrop="static"
            keyboard={true}
            className="reference-document-modal"
        >
            <Modal.Header closeButton className="border-bottom">
                <div className="d-flex align-items-center gap-3">
                    <div className="bg-primary bg-opacity-10 rounded-circle p-2 d-flex align-items-center justify-content-center w-40px h-40px">
                        <Image size={20} className="text-primary" />
                    </div>
                    <Modal.Title className="fw-bold">
                        Reference Image
                    </Modal.Title>
                </div>
            </Modal.Header>

            <Modal.Body className="p-4 text-center">
                {fileUrl && !imageError ? (
                    <div className="position-relative">
                        {isLoading && (
                            <div className="d-flex flex-column align-items-center justify-content-center py-5">
                                <Spinner animation="border" variant="primary" />
                                <p className="text-muted mt-3 mb-0">Loading image...</p>
                            </div>
                        )}

                        <img
                            src={fileUrl}
                            alt="Reference Image"
                            className={`img-fluid rounded-3 ${isLoading ? "d-none" : ""
                                }`}
                            style={{ maxHeight: "50vh" }}
                            onLoad={() => setIsLoading(false)}
                            onError={() => {
                                setIsLoading(false);
                                setImageError(true);
                            }}
                        />
                    </div>
                ) : (
                    <div className="py-5 d-flex flex-column align-items-center gap-3">
                        <AlertCircle size={48} className="text-muted opacity-50" />
                        <p className="text-muted mb-0 fw-semibold">
                            {fileUrl
                                ? "Unable to load image"
                                : "No image available"}
                        </p>
                        {fileUrl && (
                            <p className="text-muted fs-8 mb-0">
                                The image may have been moved, deleted, or the URL may be invalid.
                            </p>
                        )}
                    </div>
                )}
            </Modal.Body>

            <Modal.Footer className="border-top">
                <Button variant="secondary" size="sm" onClick={onClose}>
                    Close
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

// Hook to manage modal state
export const useScoreSliderImagesModal = () => {
    const [isOpen, setIsOpen] = useState(false);

    return {
        isOpen,
        openModal: () => setIsOpen(true),
        closeModal: () => setIsOpen(false),
    };
};